// src/services/philhealthContributionTable.js

/**
 * Client-side reader + resolver for the GLOBAL `contributionTables/philhealth`
 * collection published by the platform app (BL-062). This is the sahod-app
 * consumer (BL-073): it powers the salary-derived PhilHealth suggestion chip at
 * employee setup — the PhilHealth twin of sssContributionTable.js (BL-063).
 *
 * Historically the engine never did a live PhilHealth lookup — payroll reads the frozen
 * `employment.philhealthContribution`. The formula only matters at ONE moment:
 * employee setup, deciding what to SUGGEST for that saved figure. So this service
 * is read-only and suggestion-only; it never writes.
 *
 * PhilHealth is a flat-percentage formula, not a bracket table:
 *   premium        = rate × clamp(monthlyGross, incomeFloor, incomeCeiling)
 *   employee share = premium × employeeShare
 * Rounding is round-then-split (C11): round the premium to 2dp first, then apply
 * the employee share and round again — matching PhilHealth's published tables.
 *
 * Version resolution + the monthly-salary basis are shared with the SSS consumer
 * via contributionTableClient.js — ONE definition, so the two chips can never
 * disagree for daily-rate employees.
 *
 * Fallback / fail-closed: returns null (→ no chip, manual entry) when no version
 * resolves, params are missing/malformed, or the salary can't resolve.
 */

import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { round2, monthlyGrossForEmployee } from './contributionTableClient';

/** @param {*} v @returns {boolean} true iff v is a finite number. */
function isFiniteNum(v) {
  return typeof v === 'number' && isFinite(v);
}

/**
 * Read every published PhilHealth contribution-table version. Cardinality is a
 * handful per decade, so read them all and resolve in memory. Each returned
 * object is the stored version doc with `effectiveDate` taken from the doc id.
 *
 * @returns {Promise<Array<Object>>} version docs (may be empty)
 */
export async function fetchPhilhealthTableVersions() {
  const snap = await getDocs(
    collection(db, 'contributionTables', 'philhealth', 'versions')
  );
  return snap.docs.map((d) => ({ effectiveDate: d.id, ...d.data() }));
}

/**
 * The suggested employee PhilHealth contribution for a monthly compensation
 * against an in-force version's params. Round-then-split (C11). FAILS CLOSED —
 * returns null when the version/params are missing, any param is not a finite
 * number, `incomeCeiling ≤ incomeFloor`, the gross is unusable, or the result is
 * NaN. A legitimate `employeeShare = 0` computes 0.00 and is returned (not
 * nulled) — a real transcribed value.
 *
 * @param {?Object} version - an in-force version doc (with a `params` object)
 * @param {number} monthlyGross - monthly compensation in pesos
 * @returns {?number} the employee PhilHealth contribution, or null
 */
export function computePhilhealthEmployeeShare(version, monthlyGross) {
  if (!version || !version.params) return null;
  const { rate, incomeFloor, incomeCeiling, employeeShare } = version.params;
  if (![rate, incomeFloor, incomeCeiling, employeeShare].every(isFiniteNum)) {
    return null;
  }
  if (!(incomeCeiling > incomeFloor)) return null;
  if (typeof monthlyGross !== 'number' || !isFinite(monthlyGross) ||
      monthlyGross < 0) {
    return null;
  }
  const clamped = Math.min(Math.max(monthlyGross, incomeFloor), incomeCeiling);
  const premium = round2(rate * clamped);
  const employee = round2(premium * employeeShare);
  return isFinite(employee) ? employee : null;
}

/**
 * The suggested PhilHealth contribution for an employee against an in-force
 * version. Composes monthlyGrossForEmployee + computePhilhealthEmployeeShare.
 * Returns null (→ no suggestion, manual entry) whenever the salary can't be
 * resolved to a monthly gross or the version/params are unusable.
 *
 * @param {Object} args
 * @param {?Object} args.version - in-force version doc (null → null)
 * @param {string} args.salaryType
 * @param {number|string} [args.monthlySalary]
 * @param {number|string} [args.dailySalary]
 * @param {number|string} [args.dailyRateFactor]
 * @returns {?number} suggested employee PhilHealth contribution, or null
 */
export function suggestEmployeePhilhealth({
  version,
  salaryType,
  monthlySalary,
  dailySalary,
  dailyRateFactor,
}) {
  const gross = monthlyGrossForEmployee({
    salaryType,
    monthlySalary,
    dailySalary,
    dailyRateFactor,
  });
  if (gross === null) return null;
  return computePhilhealthEmployeeShare(version, gross);
}
